import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of as observableOf } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { RemoveModalComponent } from '../../../shared/components/remove-modal/remove-modal.component';
import * as layoutActions from '../layout/layout.actions';
import * as excludedDispositionActions from './excluded-disposition.actions';
import { ExcludedDisposition } from './excluded-disposition.model';

@Injectable()
export class ExcludedDispositionEffects {
     list$: Observable<Action> = createEffect(() => this.actions$
        .pipe(
            ofType(excludedDispositionActions.LIST),
            map((action: excludedDispositionActions.ListAction) => action.payload),
            switchMap((studioId) => this.http.get<ExcludedDisposition[]>(`/api/studios/${studioId}/excluded-dispositions`).pipe(
                map(res => res.map((u) => new ExcludedDisposition(u))),
                map((dispositions: ExcludedDisposition[]) => new excludedDispositionActions.ListCompleteAction(dispositions)),
                catchError((error) => observableOf(new excludedDispositionActions.ListFailedAction({ error }))))
            )));

     listFailed$: Observable<Action> = createEffect(() => this.actions$
        .pipe(
            ofType(excludedDispositionActions.LIST_FAILED),
            map((action: excludedDispositionActions.ListFailedAction) => action.payload),
            map(payload => new layoutActions.ErrorAction(payload))));

     create$: Observable<Action> = createEffect(() => this.actions$
        .pipe(
            ofType(excludedDispositionActions.ADD),
            map((action: excludedDispositionActions.AddAction) => action.payload),
            switchMap((disposition) => this.http.post(`/api/studios/${disposition.studio_id}/excluded-disposition`, disposition).pipe(
                map((res) => new excludedDispositionActions.AddCompleteAction(new ExcludedDisposition(res))),
                catchError(error => {
                    return observableOf(new excludedDispositionActions.AddFailedAction({ error }))
                }))
            )));

     addFailed$: Observable<Action> = createEffect(() => this.actions$
        .pipe(
            ofType(excludedDispositionActions.ADD_FAILED),
            map((action: excludedDispositionActions.AddFailedAction) => action.payload),
            map(payload => new layoutActions.ErrorAction(payload))));

     update$: Observable<Action> = createEffect(() => this.actions$
        .pipe(
            ofType(excludedDispositionActions.UPDATE),
            map((action: excludedDispositionActions.UpdateAction) => action.payload),
            mergeMap((disposition) => this.http.put(`/api/studios/${disposition.studio_id}/excluded-disposition`, disposition).pipe(
                map((res) => new excludedDispositionActions.UpdateCompleteAction(new ExcludedDisposition(res))),
                catchError(error => {
                    return observableOf(new excludedDispositionActions.UpdateFailedAction({ error }))
                }))
            )));

     updateComplete$: Observable<Action> = createEffect(() => this.actions$
        .pipe(
            ofType(excludedDispositionActions.UPDATE_COMPLETE),
            map(() => new layoutActions.InfoAction({ message: 'Excluded disposition has been updated.' }))));

     delete$: Observable<Action> = createEffect(() => this.actions$
        .pipe(
            ofType(excludedDispositionActions.DELETE),
            map((action: excludedDispositionActions.DeleteAction) => action.payload),

            switchMap((id) => this.http.delete(`/api/studios/excluded-disposition/${id}`).pipe(
                map((assets: any[]) => {
                    if (assets.length > 0) {
                        // dispostion is being used
                        this.dialog.open(RemoveModalComponent, {
                            data: {
                                title: 'Cannot Remove Excluded Disposition',
                                message: 'This Disposition cannot be removed because it is currently associated with one or more Excluded Transactions in the following locations:',
                                assets: assets
                            }
                        });
                        return new layoutActions.InfoDisplayedAction();
                    }
                    else {
                        return new excludedDispositionActions.DeleteCompleteAction(id);
                    }
                }),

                catchError((error) => observableOf(new excludedDispositionActions.DeleteFailedAction({ error }))))
            )));

     deleteComplete$: Observable<Action> = createEffect(() => this.actions$
        .pipe(
            ofType(excludedDispositionActions.DELETE_COMPLETE),
            map(() => new layoutActions.InfoAction({ message: 'Excluded disposition has been deleted.' }))));

     updateFailed$: Observable<Action> = createEffect(() => this.actions$
        .pipe(
            ofType(excludedDispositionActions.UPDATE_FAILED, excludedDispositionActions.DELETE_FAILED),
            map((action: any) => action.payload),
            map(payload => new layoutActions.ErrorAction(payload))));

    constructor(private actions$: Actions, private http: HttpClient, private dialog: MatDialog) { }
}
